import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import classnames from 'classnames';
import noop from 'lodash/noop';
import onClickOutside from '@adsk/react-onclickoutside';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import infoCircleSmall from '@adsk/bim360-matrix-react-assets/info-circle-small.svg';
import './_InfoPopover.scss';

/**
 * A container which shows a message upon hover or click
 */
class InfoPopover extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMouseOver: false,
      isClicked: false
    };
    this.onClick = this.onClick.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.setPopoverNode = this.setPopoverNode.bind(this);
  }

  onClick(event) {
    if (!this.state.isClicked && this.props.shouldOpenOnClick) {
      this.setState({ isClicked: true });
    }

    this.props.onClick(event);
  }

  onMouseEnter() {
    if (!this.state.isMouseOver) {
      this.setState({ isMouseOver: true });
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  onMouseLeave() {
    this.hideTimeout = setTimeout(() => {
      if (this.state.isMouseOver) {
        this.setState({ isMouseOver: false });
      }
      delete this.hideTimeout;
    }, 100);
  }

  handleClickOutside(e) {
    if (!this.popoverDOM || !this.popoverDOM.contains(e.target)) {
      this.setState({ isMouseOver: false, isClicked: false });
    }
  }

  setPopoverNode(node) {
    // eslint-disable-next-line react/no-find-dom-node
    this.popoverDOM = node ? findDOMNode(node) : node;
  }

  render() {
    const isOpen = this.state.isClicked || this.state.isMouseOver;
    const classes = classnames('InfoPopover', this.props.className, {
      'InfoPopover--active': isOpen
    });
    const tooltipClassName = classnames(
      'InfoPopover__popover-container',
      this.props.tooltipClassName
    );

    return (
      <div className={classes} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <Tooltip
          content={this.props.message}
          className="InfoPopover__popover"
          tooltipClassName={tooltipClassName}
          show={isOpen}
          container={this.props.container}
          popoverOnMouseEnter={this.onMouseEnter}
          popoverOnMouseLeave={this.onMouseLeave}
          overlayRef={this.setPopoverNode}
          containerPadding={this.props.containerPadding}
          place={this.props.place}
          delayHide={1000}
          delayShow={1000}
        >
          <IconButton onClick={this.onClick} className="InfoPopover__icon-button">
            <SvgIcon height={this.props.iconHeight} width={this.props.iconWidth}>
              {this.props.icon}
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </div>
    );
  }
}

InfoPopover.defaultProps = {
  iconWidth: '12px',
  iconHeight: '12px',
  onClick: noop,
  shouldOpenOnClick: true,
  icon: infoCircleSmall,
  place: Tooltip.Places.BOTTOM
};

InfoPopover.propTypes = {
  /**
   * A node to be shown on hover.
   */
  message: PropTypes.node.isRequired,
  /**
   * className to add to the InfoPopover component
   */
  className: PropTypes.string,
  /**
   * className to add to the popover container
   */
  tooltipClassName: PropTypes.string,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Minimum spacing in pixels between container border and component border.
   */
  containerPadding: PropTypes.number,
  /**
   * The icon width
   */
  iconWidth: PropTypes.string,
  /**
   * The icon height
   */
  iconHeight: PropTypes.string,
  /**
   * Optional function to run when the info icon is pressed. Passes the click event target
   */
  onClick: PropTypes.func,
  /**
   * Optional parameter to specify if InfoPopover will stay opened on click.
   */
  shouldOpenOnClick: PropTypes.bool,
  /**
   * The icon to be displayed for the info
   */
  icon: PropTypes.func,
  /**
   * Tooltip place
   */
  place: PropTypes.oneOf(Object.values(Tooltip.Places))
};

export default onClickOutside(InfoPopover);
